import { formatShortDate, parseIsoDate } from './listing-model';
import { screeningLabels, type ApplicantStatus } from './continuation-query';

/**
 * The path to the keys (spec §6), derived only from the status response so every stop is
 * something the server actually knows. Pure, so the stops are tested without rendering.
 */
export type JourneyStopKey = 'sent' | 'screening' | 'decision' | 'lease' | 'move';
export type JourneyStopState = 'done' | 'current' | 'upcoming';

export interface JourneyStop {
  key: JourneyStopKey;
  label: string;
  /** The quiet line under the label: a date, whose turn it is, or the agreement's state. */
  detail: string | null;
  state: JourneyStopState;
  /** The applicant has something to do at this stop. */
  yourTurn: boolean;
}

export interface Journey {
  stops: JourneyStop[];
  /** The stop the fill reaches; null once the application ended (declined or removed). */
  current: JourneyStop | null;
  /** 0–1, how far the track's fill runs: to the current stop, or the last done stop. */
  progress: number;
}

const stageDetail: Record<ApplicantStatus['stage'], string> = {
  ApplicationSent: 'Waiting to start', InProgress: 'In progress', IdentityVerification: 'Identity verification',
  Decision: 'Under review', Approved: 'Approved', Declined: 'Declined', Deleted: 'Unavailable',
};

export const agreementLabels: Record<NonNullable<ApplicantStatus['continuation']>['agreementState'], string> = {
  NotReady: 'Not ready', Preparing: 'Preparing', AwaitingSignatures: 'Waiting for signatures', Executed: 'Signed and confirmed', Unavailable: 'Confirmation unavailable',
};

const screeningDetail = {
  action_required: 'Your turn', waiting_on_provider: 'Being prepared', in_progress: 'With Checkr', completed: 'Done', needs_follow_up: 'Needs attention',
} as const;

function day(value: string | null | undefined, today: Date): string | null {
  const date = parseIsoDate(value);
  return date ? formatShortDate(date, today) : null;
}

/** Whether the application ended without a home; the page then shows today's copy and no journey. */
export function journeyEnded(status: Pick<ApplicantStatus, 'stage'>): boolean {
  return status.stage === 'Declined' || status.stage === 'Deleted';
}

export function applicantJourney(status: ApplicantStatus, businessName: string, today: Date = new Date()): Journey {
  const ended = journeyEnded(status);
  const approved = status.stage === 'Approved';
  const continuation = status.continuation ?? null;
  const stops: Array<Omit<JourneyStop, 'state'> & { done: boolean }> = [];

  stops.push({ key: 'sent', label: 'Sent', detail: day(status.submittedAtUtc, today), yourTurn: false, done: true });

  // Screening appears only when the business requires it.
  if (status.screening) {
    const state = status.screening.state;
    stops.push({ key: 'screening', label: 'Screening', detail: screeningDetail[state], yourTurn: state === 'action_required', done: state === 'completed' || approved });
  } else if (status.screeningStatus !== 'NotRequired') {
    stops.push({ key: 'screening', label: 'Screening', detail: screeningLabels[status.screeningStatus], yourTurn: false, done: status.screeningStatus === 'Completed' || approved });
  }

  stops.push({ key: 'decision', label: 'Decision', detail: approved ? 'Approved' : businessName || null, yourTurn: false, done: approved });

  const agreement = continuation?.agreementState;
  const next = continuation?.nextAction;
  const signed = agreement === 'Executed' || next === 'OpenResident';
  stops.push({
    key: 'lease',
    label: 'Sign lease',
    detail: agreement && agreement !== 'NotReady' ? agreementLabels[agreement]
      : continuation?.signingDelivery === 'Email' ? 'By email' : 'Online',
    yourTurn: next === 'SignAgreement' || next === 'CheckSigningEmail',
    done: signed,
  });

  // The move-in date is the applicant's request until a lease exists.
  const moveIn = day(status.expectedMoveInDate, today);
  stops.push({
    key: 'move',
    label: moveIn && !status.hasLease ? `Move in · ${moveIn}` : 'Move in',
    detail: moveIn && status.hasLease ? moveIn : null,
    yourTurn: next === 'OpenResident',
    done: false,
  });

  if (ended) {
    return { stops: stops.map(({ done, ...stop }) => ({ ...stop, state: done ? 'done' : 'upcoming' })), current: null, progress: 0 };
  }

  const currentIndex = stops.findIndex((stop) => !stop.done);
  const resolved: JourneyStop[] = stops.map(({ done, ...stop }, index) => ({
    ...stop,
    detail: stop.key === 'decision' && index === currentIndex ? stageDetail[status.stage] : stop.detail,
    state: done ? 'done' : index === currentIndex ? 'current' : 'upcoming',
  }));
  const reach = currentIndex === -1 ? stops.length - 1 : currentIndex;
  return {
    stops: resolved,
    current: currentIndex === -1 ? null : resolved[currentIndex],
    progress: stops.length > 1 ? reach / (stops.length - 1) : 1,
  };
}

/** The destination photo's chip: the requested day, or the keys once a lease exists. */
export function destinationLabel(status: Pick<ApplicantStatus, 'expectedMoveInDate' | 'hasLease'>, today: Date = new Date()): string | null {
  const moveIn = day(status.expectedMoveInDate, today);
  if (!moveIn) return null;
  return status.hasLease ? `Keys on ${moveIn}` : `Your move-in date · ${moveIn}`;
}
